import { isAdditionalBuilderStep, isCustomBuilderStep } from "@/client/constants/builder-steps";
import { useStepFormResume } from "@/client/providers/step-form";

import { SectionTransition } from "../../_components/section-transition";
import { FinalizationStep } from "./finalization-step";
import { TemplatesStep } from "./templates-step";
import { AdditionalSectionPage } from "./sections/additional/section-page";
import { BasicsSection } from "./sections/basics";
import { EducationInlineSection } from "./sections/education-inline";
import { ExperienceInlineSection } from "./sections/experience-inline";
import { LanguagesInlineSection } from "./sections/languages-inline";
import { SkillsInlineSection } from "./sections/skills-inline";
import { SummaryInlineSection } from "./sections/summary-inline";

type Introduction = {
  eyebrow: string;
  title: string;
  description: string;
  points: [string, string, string];
};

const INTRODUCTIONS: Record<string, Introduction> = {
  experience: {
    eyebrow: "Expérience",
    title: "Présentez votre parcours professionnel",
    description:
      "Ajoutez les postes, stages ou missions qui démontrent votre savoir-faire. Mettez surtout en avant vos responsabilités, vos résultats et l’impact de votre travail.",
    points: [
      "Commencez par l’expérience la plus récente",
      "Décrivez vos missions avec des verbes d’action",
      "Ajoutez des résultats concrets lorsque cela est possible",
    ],
  },

  education: {
    eyebrow: "Formation",
    title: "Valorisez votre parcours académique",
    description:
      "Présentez les diplômes et formations qui renforcent votre candidature. Les informations les plus récentes et les plus pertinentes doivent apparaître en premier.",
    points: [
      "Indiquez clairement le diplôme obtenu",
      "Ajoutez l’établissement et la période",
      "Mentionnez une spécialisation pertinente",
    ],
  },

  skills: {
    eyebrow: "Compétences",
    title: "Mettez en avant vos savoir-faire",
    description:
      "Sélectionnez les compétences réellement utiles pour le poste recherché. Une liste précise et ciblée sera plus efficace qu’une longue liste trop générale.",
    points: [
      "Privilégiez les compétences liées au poste",
      "Mélangez compétences techniques et professionnelles",
      "Utilisez les termes présents dans votre secteur",
    ],
  },

  summary: {
    eyebrow: "Profil professionnel",
    title: "Présentez votre valeur en quelques lignes",
    description:
      "Rédigez une introduction courte qui résume votre profil, votre expérience et ce que vous pouvez apporter à une entreprise.",
    points: [
      "Présentez votre métier ou votre spécialité",
      "Mettez en avant votre principale force",
      "Adaptez le texte à votre objectif professionnel",
    ],
  },

  languages: {
    eyebrow: "Langues",
    title: "Indiquez les langues que vous maîtrisez",
    description:
      "Ajoutez les langues que vous utilisez dans un contexte personnel, académique ou professionnel, avec un niveau réaliste pour chacune.",
    points: [
      "Indiquez votre niveau avec précision",
      "Commencez par la langue la mieux maîtrisée",
      "Ajoutez uniquement les langues pertinentes",
    ],
  },

  projects: {
    eyebrow: "Projets",
    title: "Montrez vos réalisations concrètes",
    description:
      "Présentez les projets personnels, académiques ou professionnels qui illustrent vos compétences et votre capacité à obtenir un résultat.",
    points: [
      "Expliquez l’objectif du projet",
      "Précisez votre rôle et les outils utilisés",
      "Ajoutez un résultat ou un lien lorsque disponible",
    ],
  },

  certifications: {
    eyebrow: "Certifications",
    title: "Valorisez vos compétences certifiées",
    description:
      "Ajoutez les certifications qui renforcent votre crédibilité et démontrent une maîtrise reconnue dans votre domaine.",
    points: [
      "Indiquez le nom exact de la certification",
      "Ajoutez l’organisme qui l’a délivrée",
      "Précisez la date et le lien de vérification",
    ],
  },

  volunteer: {
    eyebrow: "Bénévolat",
    title: "Présentez vos engagements",
    description:
      "Mettez en valeur les expériences bénévoles qui démontrent votre sens des responsabilités, votre esprit d’équipe ou votre engagement.",
    points: [
      "Décrivez clairement votre rôle",
      "Présentez les actions réalisées",
      "Soulignez les compétences développées",
    ],
  },

  awards: {
    eyebrow: "Récompenses",
    title: "Mettez vos distinctions en valeur",
    description:
      "Présentez les prix, distinctions ou reconnaissances qui montrent la qualité de votre travail et votre niveau d’excellence.",
    points: [
      "Utilisez le nom officiel de la distinction",
      "Indiquez l’organisme ou l’événement",
      "Expliquez brièvement pourquoi vous l’avez reçue",
    ],
  },

  interests: {
    eyebrow: "Centres d’intérêt",
    title: "Ajoutez une touche personnelle pertinente",
    description:
      "Choisissez des centres d’intérêt authentiques qui permettent au recruteur de mieux comprendre votre personnalité.",
    points: [
      "Restez sincère et précis",
      "Évitez les formulations trop générales",
      "Privilégiez les activités qui apportent du contexte",
    ],
  },

  publications: {
    eyebrow: "Publications",
    title: "Partagez vos travaux publiés",
    description:
      "Ajoutez vos articles, recherches, ouvrages ou contenus professionnels afin de valoriser votre expertise et votre capacité à transmettre.",
    points: [
      "Indiquez le titre exact de la publication",
      "Ajoutez la date et l’éditeur",
      "Fournissez un lien lorsque la publication est accessible",
    ],
  },

  references: {
    eyebrow: "Références",
    title: "Renforcez la confiance des recruteurs",
    description:
      "Ajoutez une personne capable de confirmer votre expérience et vos qualités professionnelles, uniquement avec son accord.",
    points: [
      "Choisissez une référence professionnelle pertinente",
      "Indiquez sa fonction et son entreprise",
      "Vérifiez ses coordonnées avant de publier",
    ],
  },
};

export const LeftSidebar = () => {
  const { selector, phase, showForm } = useStepFormResume();

  if (selector === "templates") {
    return <TemplatesStep />;
  }

  /*
   * Finalisation doit toujours afficher
   * son véritable contenu. Elle ne passe
   * jamais par SectionTransition.
   */
  if (selector === "finalization") {
    return (
      <div className="mx-auto w-full max-w-[760px] py-2 sm:py-4">
        <FinalizationStep />
      </div>
    );
  }

  const shouldShowIntroduction =
    phase === "intro" && selector !== "basics" && !isCustomBuilderStep(selector);

  if (shouldShowIntroduction) {
    const introduction = INTRODUCTIONS[selector] ?? {
      eyebrow: "Nouvelle section",
      title: "Complétez cette rubrique",
      description:
        "Ajoutez des informations claires et utiles afin de renforcer la qualité de votre CV.",
      points: [
        "Restez précis et professionnel",
        "Privilégiez les informations pertinentes",
        "Vous pourrez modifier vos réponses",
      ],
    };

    return (
      <SectionTransition
        eyebrow={introduction.eyebrow}
        title={introduction.title}
        description={introduction.description}
        points={introduction.points}
        onContinue={showForm}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[760px] py-2 sm:py-4">
      {selector === "basics" && <BasicsSection />}

      {selector === "experience" && <ExperienceInlineSection />}

      {selector === "education" && <EducationInlineSection />}

      {selector === "skills" && <SkillsInlineSection />}

      {selector === "summary" && <SummaryInlineSection />}

      {selector === "languages" && <LanguagesInlineSection />}

      {(isAdditionalBuilderStep(selector) || isCustomBuilderStep(selector)) && (
        <AdditionalSectionPage id={selector} />
      )}
    </div>
  );
};
